// ─── src/GewOverviewView.tsx ─────────────────────────────────
//
// Admin landing view for the GEW & Conference section. One page, three
// panels: hero KPI strip, participants panel (filterable), and partners
// panel (three tabs: Portal · Legacy · Catalysts). Reads live
// aggregates from /gew-overview; never mutates — every write-side
// action still lives inside its own dedicated view.

import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Handshake,
  MapPin,
  Megaphone,
  Rocket,
  Search,
  Ticket,
  Users,
  UserCog,
  Venus,
} from "lucide-react";
import {
  getOverviewParticipants,
  getOverviewPartners,
  type OverviewCatalyst,
  type OverviewLegacyPartner,
  type OverviewParticipantsPayload,
  type OverviewPartnersPayload,
  type OverviewPortalPartner,
} from "./api/gewOverview";
import { Pagination } from "./components/dashboard/Pagination";
import { RegistrationsPerDayChart } from "./components/dashboard/RegistrationsPerDayChart";
import { HorizontalBarChart } from "./components/dashboard/HorizontalBarChart";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import { toChartEntries } from "./types/stats";
import type { GewParticipant } from "./api/gew";

const LIMIT = 20;

const PORTAL_STATUS_COLOR: Record<string, string> = {
  draft:
    "bg-brand-border/40 text-brand-muted border border-brand-border",
  under_review:
    "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  info_required:
    "bg-orange-500/15 text-orange-300 border border-orange-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  waitlist: "bg-sky-500/15 text-sky-300 border border-sky-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  suspended:
    "bg-brand-border/40 text-brand-muted border border-brand-border",
};

const EVENT_STATUS_COLOR: Record<string, string> = {
  draft: "text-brand-muted",
  submitted: "text-amber-300",
  edits_required: "text-orange-300",
  approved: "text-emerald-300",
  published: "text-emerald-300",
  executed: "text-sky-300",
  report_pending: "text-amber-300",
  completed: "text-emerald-200",
  cancelled: "text-rose-300",
};

export default function GewOverviewView() {
  const { data: participants } = useQuery({
    queryKey: ["gew-overview", "participants"],
    queryFn: getOverviewParticipants,
    refetchOnWindowFocus: false,
  });

  // Hero stats are aggregated live on every request — poll so the
  // numbers keep moving while the page is open.
  const { data: partners, isLoading: prLoading } = useQuery({
    queryKey: ["gew-overview", "partners"],
    queryFn: getOverviewPartners,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">
          GEW & Conference · Overview
        </h1>
        <p className="mt-1 text-sm text-brand-muted">
          Snapshot of every participant, partner, and catalyst meetup
          feeding the GEW Syria 2026 cycle. Event and attendee counts
          are gated to{" "}
          <span className="font-mono text-white">1 – 5 Nov 2026</span>{" "}
          — older catalyst meetups don't count toward "active events"
          or attendee totals. Read-only — approve/reject stays in each
          dedicated view.
        </p>
      </div>

      <HeroStrip partners={partners} loading={prLoading} />

      {/* <CityDistributions data={partners} /> — hidden for now */}

      <ParticipantsPanel data={participants} />

      <PartnersPanel data={partners} loading={prLoading} />
    </div>
  );
}

// ═══ Hero ═════════════════════════════════════════════════════════

function HeroStrip({
  partners,
  loading,
}: {
  partners?: OverviewPartnersPayload;
  loading: boolean;
}) {
  const t = partners?.totals;
  const totalRegistered =
    (t?.totalEventAttendees ?? 0) + (t?.totalMeetupAttendees ?? 0);
  const totalEvents = (t?.activeEventCount ?? 0) + (t?.meetupCount ?? 0);
  const totalAccepted = t?.totalAccepted ?? 0;
  const acceptanceRate =
    totalRegistered > 0
      ? `${Math.round((totalAccepted / totalRegistered) * 100)}%`
      : "—";
  const v = (n: number | string) => (loading ? "…" : n);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label="Total Registered"
        value={v(totalRegistered)}
        icon={Users}
        accent="bg-brand-teal"
      />
      <StatCard
        label={`Acceptance rate · ${totalAccepted} / ${totalRegistered}`}
        value={v(acceptanceRate)}
        icon={CheckCircle2}
        accent="bg-brand-orange"
      />
      <StatCard
        label="Approved partners"
        value={v(t?.approvedPortalCount ?? 0)}
        icon={BadgeCheck}
        accent="bg-brand-green"
      />
      <StatCard
        label="Partners with published events"
        value={v(t?.publishedPartnerCount ?? 0)}
        icon={Megaphone}
        accent="bg-brand-green"
      />
      <StatCard
        label="Total events (catalyst + partner)"
        value={v(totalEvents)}
        icon={CalendarDays}
        accent="bg-brand-teal"
      />
      <div title={(t?.eventCities ?? []).join("، ")}>
        <StatCard
          label="Cities with events"
          value={v(t?.eventCityCount ?? 0)}
          icon={MapPin}
          accent="bg-brand-teal"
        />
      </div>
      <FemaleRatioCard
        gender={partners?.totals.attendeeGender}
        loading={loading}
      />
    </div>
  );
}

/**
 * Female share of event attendees (partner events + catalyst meetups),
 * not of GEW participants. Denominator is every attendee registration,
 * so "prefer not to say" counts against the ratio rather than inflating
 * it; the split bar shows that remainder explicitly.
 */
function FemaleRatioCard({
  gender,
  loading,
}: {
  gender?: OverviewPartnersPayload["totals"]["attendeeGender"];
  loading: boolean;
}) {
  const total = gender?.total ?? 0;
  const pct = (n: number) => (total ? (n / total) * 100 : 0);
  const female = pct(gender?.female ?? 0);
  const male = pct(gender?.male ?? 0);
  return (
    <div className="card col-span-2 flex flex-col justify-center gap-2 p-5 lg:col-span-1">
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-pink-500/80">
          <Venus size={22} className="text-white" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm text-brand-muted">Female attendees</p>
          <p className="text-2xl font-bold text-white">
            {loading || !gender ? "…" : total ? `${Math.round(female)}%` : "—"}
          </p>
        </div>
      </div>
      {gender && total > 0 && (
        <>
          <div
            className="flex h-1.5 w-full overflow-hidden rounded-full bg-brand-border/60"
            aria-hidden
          >
            <div className="bg-pink-400" style={{ width: `${female}%` }} />
            <div className="bg-sky-400" style={{ width: `${male}%` }} />
          </div>
          <p className="text-[11px] text-brand-muted">
            {gender.female} F · {gender.male} M
            {gender.unspecified > 0 && ` · ${gender.unspecified} n/a`} of {total}
          </p>
        </>
      )}
    </div>
  );
}

// ═══ Distribution charts ══════════════════════════════════════════

/**
 * Side-by-side horizontal bar charts answering the two questions ops
 * actually asks about every event week: where do the partners come
 * from, and where do the registered attendees live? Partner totals
 * fold legacy + portal rows into one count per governorate; attendee
 * totals combine partner-event attendees and (window-filtered)
 * meetup attendees so both pools surface on the same chart.
 */
function CityDistributions({ data }: { data?: OverviewPartnersPayload }) {
  const partnersEntries = useMemo(() => {
    const map = data?.distributions.partnersByGovernorate ?? {};
    const total = Object.values(map).reduce((s, n) => s + n, 0);
    return toChartEntries(
      Object.entries(map).map(([name, count]) => ({ name, count })),
      total,
    );
  }, [data]);

  const attendeesEntries = useMemo(() => {
    const map = data?.distributions.attendeesByGovernorate ?? {};
    const total = Object.values(map).reduce((s, n) => s + n, 0);
    return toChartEntries(
      Object.entries(map).map(([name, count]) => ({ name, count })),
      total,
    );
  }, [data]);

  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <ChartCard
        title="Partners per city"
        caption={`${partnersEntries.length} cities covered · ${
          partnersEntries.reduce((s, e) => s + e.value, 0)
        } partners`}
        entries={partnersEntries}
        emptyLabel="No partner data yet."
      />
      <ChartCard
        title="Attendees per city"
        caption={`${attendeesEntries.length} cities · ${
          attendeesEntries.reduce((s, e) => s + e.value, 0)
        } attendees`}
        entries={attendeesEntries}
        emptyLabel="No attendee data yet."
      />
    </section>
  );
}

function ChartCard({
  title,
  caption,
  entries,
  emptyLabel,
}: {
  title: string;
  caption: string;
  entries: Array<{ name: string; value: number; percentage: string }>;
  emptyLabel: string;
}) {
  return (
    <div className="card space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-brand-muted">
          {title}
        </p>
        <p className="text-[11px] text-brand-muted">{caption}</p>
      </div>
      <div className="h-[360px]">
        {entries.length === 0 ? (
          <p className="pt-8 text-sm text-brand-muted">{emptyLabel}</p>
        ) : (
          <HorizontalBarChart data={entries} />
        )}
      </div>
    </div>
  );
}

// ═══ Participants panel ═══════════════════════════════════════════

function ParticipantsPanel({
  data,
}: {
  data?: OverviewParticipantsPayload;
}) {
  const [search, setSearch] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [gender, setGender] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [currentStatus, setCurrentStatus] = useState("");
  const [isFounder, setIsFounder] = useState<"" | "true" | "false">("");

  const rows = data?.data ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (governorate && r.governorate !== governorate) return false;
      if (gender && r.gender !== gender) return false;
      if (ageGroup && r.ageGroup !== ageGroup) return false;
      if (currentStatus && r.currentStatus !== currentStatus) return false;
      if (isFounder === "true" && !r.isFounder) return false;
      if (isFounder === "false" && r.isFounder) return false;
      if (!q) return true;
      const bag = [
        r.fullName,
        r.email,
        r.whatsapp,
        r.governorate,
        r.cityArea,
        r.currentStatus,
        (r.skills ?? []).join(" "),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return bag.includes(q);
    });
  }, [rows, search, governorate, gender, ageGroup, currentStatus, isFounder]);

  const resetFilters = () => {
    setSearch("");
    setGovernorate("");
    setGender("");
    setAgeGroup("");
    setCurrentStatus("");
    setIsFounder("");
  };

  const govOptions = Object.entries(data?.byGovernorate ?? {}).sort(
    (a, b) => b[1] - a[1],
  );
  const genderOptions = Object.keys(data?.byGender ?? {}).sort();
  const ageOptions = Object.keys(data?.byAgeGroup ?? {}).sort();
  const statusOptions = Object.keys(data?.byCurrentStatus ?? {}).sort();
  const topSkills = Object.entries(data?.bySkills ?? {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">Participants</h2>
          <p className="text-sm text-brand-muted">
            Pulled from{" "}
            <code className="rounded bg-brand-border/40 px-1 text-brand-muted">
              gew_participant_registrations
            </code>{" "}
            — fed by every attendee registration and the standalone
            form.
          </p>
        </div>
        <button
          type="button"
          onClick={() => exportParticipantsCsv(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm font-semibold text-brand-muted hover:text-white hover:border-brand-orange transition-colors disabled:opacity-50"
        >
          <Download size={13} /> CSV ({filtered.length})
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total" value={data?.total ?? 0} icon={Ticket} accent="bg-brand-orange" />
        <StatCard label="Founders" value={data?.founders ?? 0} icon={Rocket} accent="bg-brand-green" />
        <StatCard label="Cities" value={govOptions.length} icon={MapPin} accent="bg-brand-teal" />
        <StatCard
          label="Skills tallied"
          value={Object.keys(data?.bySkills ?? {}).length}
          icon={Users}
          accent="bg-brand-teal"
        />
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, whatsapp, city, skill…"
            className="w-full rounded-lg border border-brand-border bg-brand-navy pl-9 pr-3 py-2 text-sm text-white placeholder-brand-muted outline-none focus:border-brand-green"
          />
        </div>
        <FilterSelect
          value={governorate}
          onChange={setGovernorate}
          allLabel="All governorates"
          options={govOptions.map(([v, n]) => ({ value: v, label: `${v} (${n})` }))}
        />
        <FilterSelect
          value={gender}
          onChange={setGender}
          allLabel="All genders"
          options={genderOptions.map((g) => ({ value: g, label: g }))}
        />
        <FilterSelect
          value={ageGroup}
          onChange={setAgeGroup}
          allLabel="All ages"
          options={ageOptions.map((g) => ({ value: g, label: g }))}
        />
        <FilterSelect
          value={currentStatus}
          onChange={setCurrentStatus}
          allLabel="All statuses"
          options={statusOptions.map((g) => ({ value: g, label: g }))}
        />
        <FilterSelect
          value={isFounder}
          onChange={(v) => setIsFounder(v as "" | "true" | "false")}
          allLabel="Founders & non-founders"
          options={[
            { value: "true", label: "Founders only" },
            { value: "false", label: "Non-founders" },
          ]}
        />
        <button
          type="button"
          onClick={resetFilters}
          className="rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-xs font-semibold text-brand-muted hover:text-white hover:border-brand-orange transition-colors"
        >
          Reset
        </button>
      </div>

      {/* Sparkline */}
      <RegistrationsPerDayChart
        rows={filtered}
        title={`Registrations per day · ${filtered.length} filtered`}
      />

      {/* Top skills — horizontal bars so the comparison is proportional
          at a glance, instead of a flat chip cloud where "entrepreneurship"
          and "developer" read identically. */}
      <TopSkills skills={topSkills} />
    </section>
  );
}

/**
 * Proportional-bar leaderboard for the top skills tally. Reads
 * instantly as "which skills dominate" instead of a chip wall that
 * only differentiates by the little badge count.
 */
function TopSkills({ skills }: { skills: Array<[string, number]> }) {
  if (skills.length === 0) return null;
  const max = Math.max(...skills.map(([, n]) => n));
  const grandTotal = skills.reduce((s, [, n]) => s + n, 0);
  return (
    <div className="card space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-brand-muted">
          Top skills
        </p>
        <p className="text-[11px] text-brand-muted">
          {skills.length} tracked ·{" "}
          <span className="font-mono text-white">{grandTotal}</span> tags
          across participants
        </p>
      </div>
      <ol className="space-y-2.5">
        {skills.map(([skill, count], i) => {
          const pct = max > 0 ? Math.round((count / max) * 100) : 0;
          const share =
            grandTotal > 0 ? Math.round((count / grandTotal) * 100) : 0;
          return (
            <li key={skill} className="group">
              <div className="flex items-center gap-3">
                <span
                  className="w-5 shrink-0 text-right font-mono text-[11px] text-brand-muted/70 tabular-nums"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-semibold text-white">
                      {skill}
                    </span>
                    <span className="shrink-0 text-xs">
                      <span className="font-mono tabular-nums text-brand-orange">
                        {count}
                      </span>
                      <span className="ml-1.5 text-[10px] font-mono text-brand-muted/70">
                        {share}%
                      </span>
                    </span>
                  </div>
                  <div
                    className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full"
                    style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-500"
                      style={{
                        width: `${pct}%`,
                        background:
                          "linear-gradient(90deg, #EA5A1F, #ff8a5c)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ═══ Partners panel ═══════════════════════════════════════════════

type PartnerTab = "portal" | "legacy" | "catalysts";

function PartnersPanel({
  data,
  loading,
}: {
  data?: OverviewPartnersPayload;
  loading: boolean;
}) {
  const [tab, setTab] = useState<PartnerTab>("portal");

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-white">Partners</h2>
        <p className="text-sm text-brand-muted">
          Three sources: approved partner-portal orgs, legacy inquiry
          submissions, and catalyst meetup hosts.
        </p>
      </div>

      {/* Partner totals strip */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatCard
          label="Partners + Catalyst"
          value={
            (data?.totals.portalCount ?? 0) +
            (data?.totals.legacyCount ?? 0) +
            (data?.totals.catalystCount ?? 0)
          }
          icon={Users}
          accent="bg-brand-orange"
        />
        <StatCard
          label="Portal partners"
          value={data?.totals.portalCount ?? 0}
          icon={Handshake}
          accent="bg-brand-orange"
        />
        <StatCard
          label="Legacy inquiries"
          value={data?.totals.legacyCount ?? 0}
          icon={Building2}
          accent="bg-brand-green"
        />
        <StatCard
          label="Catalyst hosts"
          value={data?.totals.catalystCount ?? 0}
          icon={UserCog}
          accent="bg-brand-teal"
        />
        <StatCard
          label="Avg attendees / event"
          value={avgPerEvent(data)}
          icon={Users}
          accent="bg-brand-teal"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-brand-border bg-brand-navy p-1 w-fit">
        <TabBtn
          active={tab === "portal"}
          onClick={() => setTab("portal")}
          label={`Portal · ${data?.totals.portalCount ?? 0}`}
        />
        <TabBtn
          active={tab === "legacy"}
          onClick={() => setTab("legacy")}
          label={`Legacy · ${data?.totals.legacyCount ?? 0}`}
        />
        <TabBtn
          active={tab === "catalysts"}
          onClick={() => setTab("catalysts")}
          label={`Catalysts · ${data?.totals.catalystCount ?? 0}`}
        />
      </div>

      {tab === "portal" && (
        <PortalPartnersTable rows={data?.portal ?? []} loading={loading} />
      )}
      {tab === "legacy" && (
        <LegacyPartnersTable rows={data?.legacy ?? []} loading={loading} />
      )}
      {tab === "catalysts" && (
        <CatalystsTable rows={data?.catalysts ?? []} loading={loading} />
      )}
    </section>
  );
}

function avgPerEvent(data?: OverviewPartnersPayload): string {
  const events =
    (data?.totals.activeEventCount ?? 0) + (data?.totals.meetupCount ?? 0);
  const atts =
    (data?.totals.totalEventAttendees ?? 0) +
    (data?.totals.totalMeetupAttendees ?? 0);
  if (events === 0) return "—";
  return Math.round(atts / events).toString();
}

// ── Portal partners ─────────────────────────────────────────────────

function PortalPartnersTable({
  rows,
  loading,
}: {
  rows: OverviewPortalPartner[];
  loading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (!q) return true;
      const bag = [
        r.nameAr,
        r.nameEn ?? "",
        r.type,
        r.city ?? "",
        r.governorate ?? "",
        r.contactName ?? "",
        r.contactEmail ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return bag.includes(q);
    });
  }, [rows, search, status]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * LIMIT, page * LIMIT),
    [filtered, page],
  );
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const clampedPage = Math.min(page, totalPages);

  const statusOptions = Array.from(new Set(rows.map((r) => r.status))).sort();

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search org, contact, email, city…"
            className="w-full rounded-lg border border-brand-border bg-brand-navy pl-9 pr-3 py-2 text-sm text-white placeholder-brand-muted outline-none focus:border-brand-green"
          />
        </div>
        <FilterSelect
          value={status}
          onChange={(v) => {
            setStatus(v);
            setPage(1);
          }}
          allLabel="All statuses"
          options={statusOptions.map((s) => ({ value: s, label: s }))}
        />
        <button
          type="button"
          onClick={() => exportPortalCsv(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm font-semibold text-brand-muted hover:text-white hover:border-brand-orange transition-colors disabled:opacity-50"
        >
          <Download size={13} /> CSV ({filtered.length})
        </button>
      </div>

      <div className="card p-0 overflow-hidden mt-3">
        {loading && <TableSkeleton />}
        {!loading && paginated.length === 0 && (
          <p className="p-6 text-sm text-brand-muted">No partners found.</p>
        )}
        {!loading && paginated.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-left text-xs uppercase text-brand-muted">
                <th className="px-3 py-3 w-8"></th>
                <th className="px-3 py-3">Organisation</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Location</th>
                <th className="px-3 py-3">Events</th>
                <th className="px-3 py-3">Capacity</th>
                <th className="px-3 py-3">Attendees</th>
                <th className="px-3 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((p) => {
                const open = expanded.has(p.id);
                return (
                  <React.Fragment key={p.id}>
                    <tr
                      className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer"
                      onClick={() => toggle(p.id)}
                    >
                      <td className="px-3 py-3 text-brand-muted">
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          {p.logoUrl ? (
                            <img
                              src={p.logoUrl}
                              alt=""
                              className="h-7 w-7 rounded border border-brand-border bg-white object-contain"
                            />
                          ) : (
                            <div className="grid h-7 w-7 place-items-center rounded border border-brand-border bg-brand-navy text-brand-muted">
                              <Building2 size={13} />
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-white">{p.nameAr}</p>
                            {p.nameEn && (
                              <p className="text-xs text-brand-muted" dir="ltr">
                                {p.nameEn}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 capitalize text-brand-muted">
                        {p.type}
                      </td>
                      <td className="px-3 py-3 text-brand-muted">
                        {[p.city, p.governorate].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-3 py-3 text-white">
                        {p.activeEventCount}
                        <span className="ml-1 text-xs text-brand-muted">
                          ({p.publishedEventCount} published · {p.eventCount} total)
                        </span>
                      </td>
                      <td className="px-3 py-3 text-white">
                        {p.totalCapacity > 0 ? p.totalCapacity : "—"}
                        {p.totalCapacity > 0 && (
                          <span className="ml-1 text-xs text-brand-muted">
                            seats ({fillPct(p.confirmedCount, p.totalCapacity)})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-white">
                        {p.totalAttendees}
                        <span className="ml-1 text-xs text-brand-muted">
                          ({p.acceptedCount} accepted · {p.checkedInCount} checked-in)
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                            PORTAL_STATUS_COLOR[p.status] ??
                            "bg-brand-border/40 text-brand-muted border border-brand-border"
                          }`}
                        >
                          {p.status}
                        </span>
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-brand-border/10">
                        <td colSpan={8} className="px-6 py-4">
                          <PortalPartnerEvents partner={p} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && total > 0 && (
        <div className="mt-3">
          <Pagination
            page={clampedPage}
            totalPages={totalPages}
            total={total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={false}
          />
        </div>
      )}
    </>
  );
}

function PortalPartnerEvents({ partner }: { partner: OverviewPortalPartner }) {
  if (partner.events.length === 0) {
    return (
      <p className="text-xs text-brand-muted">
        No events yet for this partner.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold uppercase tracking-wider text-brand-muted">
        Events ({partner.events.length})
      </h4>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-brand-border/50 text-brand-muted">
            <th className="px-2 py-2 text-left">Title</th>
            <th className="px-2 py-2 text-left">Date</th>
            <th className="px-2 py-2 text-left">Status</th>
            <th className="px-2 py-2 text-right">Capacity</th>
            <th className="px-2 py-2 text-right">Fill</th>
            <th className="px-2 py-2 text-right">Total</th>
            <th className="px-2 py-2 text-right">Confirmed</th>
            <th className="px-2 py-2 text-right">Waitlist</th>
            <th className="px-2 py-2 text-right">Accepted</th>
            <th className="px-2 py-2 text-right">Rejected</th>
            <th className="px-2 py-2 text-right">Checked-in</th>
          </tr>
        </thead>
        <tbody>
          {partner.events.map((e) => (
            <tr key={e.id} className="border-b border-brand-border/30">
              <td className="px-2 py-2 text-white">
                {e.titleAr ?? "(no title)"}
              </td>
              <td className="px-2 py-2 text-brand-muted" dir="ltr">
                {e.eventDate ?? "—"}
              </td>
              <td
                className={`px-2 py-2 font-semibold ${
                  EVENT_STATUS_COLOR[e.status] ?? "text-brand-muted"
                }`}
              >
                {e.status}
              </td>
              <CapacityCells capacity={e.capacity} confirmed={e.confirmed} />
              <td className="px-2 py-2 text-right text-white">{e.total}</td>
              <td className="px-2 py-2 text-right text-emerald-300">
                {e.confirmed}
              </td>
              <td className="px-2 py-2 text-right text-amber-300">
                {e.waitlist}
              </td>
              <td className="px-2 py-2 text-right text-emerald-200">
                {e.accepted}
              </td>
              <td className="px-2 py-2 text-right text-rose-300">
                {e.rejected}
              </td>
              <td className="px-2 py-2 text-right text-sky-300">
                {e.checkedIn}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Legacy ─────────────────────────────────────────────────────────

function LegacyPartnersTable({
  rows,
  loading,
}: {
  rows: OverviewLegacyPartner[];
  loading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.orgNameAr,
        r.orgNameEn ?? "",
        r.orgType,
        r.orgCity,
        r.coordinatorName ?? "",
        r.coordinatorEmail ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * LIMIT, page * LIMIT),
    [filtered, page],
  );
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const clampedPage = Math.min(page, totalPages);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search legacy applications…"
            className="w-full rounded-lg border border-brand-border bg-brand-navy pl-9 pr-3 py-2 text-sm text-white placeholder-brand-muted outline-none focus:border-brand-green"
          />
        </div>
        <button
          type="button"
          onClick={() => exportLegacyCsv(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm font-semibold text-brand-muted hover:text-white hover:border-brand-orange transition-colors disabled:opacity-50"
        >
          <Download size={13} /> CSV ({filtered.length})
        </button>
      </div>
      <div className="card p-0 overflow-hidden mt-3">
        {loading && <TableSkeleton />}
        {!loading && paginated.length === 0 && (
          <p className="p-6 text-sm text-brand-muted">No legacy inquiries.</p>
        )}
        {!loading && paginated.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-left text-xs uppercase text-brand-muted">
                <th className="px-3 py-3">Organisation</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">City</th>
                <th className="px-3 py-3">Coordinator</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3">Phone</th>
                <th className="px-3 py-3">Collaborated before</th>
                <th className="px-3 py-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors"
                >
                  <td className="px-3 py-3">
                    <p className="font-semibold text-white">
                      {r.orgNameAr || "—"}
                    </p>
                    {r.orgNameEn && (
                      <p className="text-xs text-brand-muted" dir="ltr">
                        {r.orgNameEn}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-3 text-brand-muted">{r.orgType || "—"}</td>
                  <td className="px-3 py-3 text-brand-muted">{r.orgCity || "—"}</td>
                  <td className="px-3 py-3 text-brand-muted">
                    {r.coordinatorName || "—"}
                  </td>
                  <td className="px-3 py-3 text-brand-muted" dir="ltr">
                    {r.coordinatorEmail || "—"}
                  </td>
                  <td className="px-3 py-3 text-brand-muted" dir="ltr">
                    {r.coordinatorPhone || "—"}
                  </td>
                  <td className="px-3 py-3 text-brand-muted">
                    {r.hasCollaboratedBefore ? "Yes" : "No"}
                  </td>
                  <td className="px-3 py-3 text-xs text-brand-muted">
                    {r.submittedAt
                      ? new Date(r.submittedAt).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!loading && total > 0 && (
        <div className="mt-3">
          <Pagination
            page={clampedPage}
            totalPages={totalPages}
            total={total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={false}
          />
        </div>
      )}
    </>
  );
}

// ── Catalysts ──────────────────────────────────────────────────────

function CatalystsTable({
  rows,
  loading,
}: {
  rows: OverviewCatalyst[];
  loading: boolean;
}) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.hostName, r.hostEmail ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const paginated = useMemo(
    () => filtered.slice((page - 1) * LIMIT, page * LIMIT),
    [filtered, page],
  );
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const clampedPage = Math.min(page, totalPages);

  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search catalyst hosts…"
            className="w-full rounded-lg border border-brand-border bg-brand-navy pl-9 pr-3 py-2 text-sm text-white placeholder-brand-muted outline-none focus:border-brand-green"
          />
        </div>
        <button
          type="button"
          onClick={() => exportCatalystsCsv(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm font-semibold text-brand-muted hover:text-white hover:border-brand-orange transition-colors disabled:opacity-50"
        >
          <Download size={13} /> CSV ({filtered.length})
        </button>
      </div>
      <div className="card p-0 overflow-hidden mt-3">
        {loading && <TableSkeleton />}
        {!loading && paginated.length === 0 && (
          <p className="p-6 text-sm text-brand-muted">No catalyst meetups.</p>
        )}
        {!loading && paginated.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-border text-left text-xs uppercase text-brand-muted">
                <th className="px-3 py-3 w-8"></th>
                <th className="px-3 py-3">Catalyst host</th>
                <th className="px-3 py-3">Meetups</th>
                <th className="px-3 py-3">Capacity</th>
                <th className="px-3 py-3">Total attendees</th>
                <th className="px-3 py-3">Accepted</th>
                <th className="px-3 py-3">Checked-in</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((c) => {
                const key = c.hostName + "|" + (c.hostEmail ?? "");
                const open = expanded.has(key);
                return (
                  <React.Fragment key={key}>
                    <tr
                      className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors cursor-pointer"
                      onClick={() => toggle(key)}
                    >
                      <td className="px-3 py-3 text-brand-muted">
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-semibold text-white">{c.hostName}</p>
                        {c.hostEmail && (
                          <p className="text-xs text-brand-muted" dir="ltr">
                            {c.hostEmail}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-3 text-white">{c.meetupCount}</td>
                      <td className="px-3 py-3 text-white">
                        {c.totalCapacity > 0 ? c.totalCapacity : "—"}
                        {c.totalCapacity > 0 && (
                          <span className="ml-1 text-xs text-brand-muted">
                            seats ({fillPct(c.confirmedCount, c.totalCapacity)})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-white">
                        {c.totalAttendees}
                      </td>
                      <td className="px-3 py-3 text-emerald-200">
                        {c.acceptedCount}
                      </td>
                      <td className="px-3 py-3 text-sky-300">
                        {c.checkedInCount}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-brand-border/10">
                        <td colSpan={7} className="px-6 py-4">
                          <CatalystMeetupsTable rows={c.meetups} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!loading && total > 0 && (
        <div className="mt-3">
          <Pagination
            page={clampedPage}
            totalPages={totalPages}
            total={total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={false}
          />
        </div>
      )}
    </>
  );
}

function CatalystMeetupsTable({
  rows,
}: {
  rows: OverviewCatalyst["meetups"];
}) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-brand-border/50 text-brand-muted">
          <th className="px-2 py-2 text-left">Title</th>
          <th className="px-2 py-2 text-left">Date</th>
          <th className="px-2 py-2 text-left">Governorate</th>
          <th className="px-2 py-2 text-right">Capacity</th>
          <th className="px-2 py-2 text-right">Fill</th>
          <th className="px-2 py-2 text-right">Total</th>
          <th className="px-2 py-2 text-right">Confirmed</th>
          <th className="px-2 py-2 text-right">Waitlist</th>
          <th className="px-2 py-2 text-right">Accepted</th>
          <th className="px-2 py-2 text-right">Checked-in</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((m) => (
          <tr key={m.id} className="border-b border-brand-border/30">
            <td className="px-2 py-2 text-white">
              {m.title ?? "(no title)"}
            </td>
            <td className="px-2 py-2 text-brand-muted" dir="ltr">
              {m.eventDate ?? "—"}
            </td>
            <td className="px-2 py-2 text-brand-muted">
              {m.governorate ?? "—"}
            </td>
            <CapacityCells capacity={m.capacity} confirmed={m.confirmed} />
            <td className="px-2 py-2 text-right text-white">{m.total}</td>
            <td className="px-2 py-2 text-right text-emerald-300">
              {m.confirmed}
            </td>
            <td className="px-2 py-2 text-right text-amber-300">
              {m.waitlist}
            </td>
            <td className="px-2 py-2 text-right text-emerald-200">
              {m.accepted}
            </td>
            <td className="px-2 py-2 text-right text-sky-300">
              {m.checkedIn}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ═══ Shared UI ════════════════════════════════════════════════════

/** Confirmed seats as a share of capacity, e.g. "64%". */
function fillPct(confirmed: number, capacity: number): string {
  if (!capacity) return "—";
  return `${Math.round((confirmed / capacity) * 100)}%`;
}

/**
 * Capacity + fill cells for a single event row. Fill is confirmed seats
 * over capacity; amber from 80%, rose once full. No cap → "Unlimited".
 */
function CapacityCells({
  capacity,
  confirmed,
}: {
  capacity: number | null;
  confirmed: number;
}) {
  if (!capacity) {
    return (
      <>
        <td className="px-2 py-2 text-right text-brand-muted">Unlimited</td>
        <td className="px-2 py-2 text-right text-brand-muted">—</td>
      </>
    );
  }
  const ratio = confirmed / capacity;
  const tone =
    ratio >= 1 ? "text-rose-300" : ratio >= 0.8 ? "text-amber-300" : "text-white";
  return (
    <>
      <td className="px-2 py-2 text-right text-white">{capacity}</td>
      <td className={`px-2 py-2 text-right font-semibold ${tone}`}>
        {fillPct(confirmed, capacity)}
      </td>
    </>
  );
}

function FilterSelect({
  value,
  onChange,
  allLabel,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-lg border border-brand-border bg-brand-navy px-3 py-2 text-sm text-white outline-none focus:border-brand-green"
    >
      <option value="" className="bg-brand-navy">
        {allLabel}
      </option>
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-brand-navy">
          {o.label}
        </option>
      ))}
    </select>
  );
}

function TabBtn({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
        active
          ? "bg-brand-green text-gray-900"
          : "text-brand-muted hover:text-white hover:bg-brand-border/40"
      }`}
    >
      {label}
    </button>
  );
}

// ═══ CSV exporters ════════════════════════════════════════════════

const esc = (v: unknown) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (header: string[], body: string[][], name: string) => {
  const blob = new Blob(
    ["﻿" + [header.join(","), ...body.map((r) => r.join(","))].join("\n")],
    { type: "text/csv;charset=utf-8;" },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

function exportParticipantsCsv(rows: GewParticipant[]) {
  const header = [
    "Full Name",
    "Gender",
    "Age Group",
    "WhatsApp",
    "Email",
    "Governorate",
    "City / Area",
    "Current Status",
    "Skills",
    "LinkedIn",
    "Is Founder",
    "Startup",
    "Submitted At",
  ];
  const body = rows.map((r) => [
    r.fullName,
    r.gender,
    r.ageGroup,
    r.whatsapp,
    r.email,
    r.governorate,
    r.cityArea,
    r.currentStatus,
    (r.skills ?? []).join(" | "),
    r.linkedInUrl ?? "",
    r.isFounder ? "Yes" : "No",
    r.startupName ?? "",
    r.createdAt,
  ].map(esc));
  downloadCsv(header, body, "gew_overview_participants");
}

function exportPortalCsv(rows: OverviewPortalPartner[]) {
  const header = [
    "Org Name (AR)",
    "Org Name (EN)",
    "Type",
    "Governorate",
    "City",
    "Status",
    "Events (total)",
    "Events (published)",
    "Capacity (seats)",
    "Attendees (total)",
    "Confirmed",
    "Waitlist",
    "Accepted",
    "Checked-in",
    "Contact",
    "Contact Email",
    "Submitted",
  ];
  const body = rows.map((r) => [
    r.nameAr,
    r.nameEn ?? "",
    r.type,
    r.governorate ?? "",
    r.city ?? "",
    r.status,
    r.eventCount,
    r.publishedEventCount,
    r.totalCapacity,
    r.totalAttendees,
    r.confirmedCount,
    r.waitlistCount,
    r.acceptedCount,
    r.checkedInCount,
    r.contactName ?? "",
    r.contactEmail ?? "",
    r.createdAt ?? "",
  ].map(esc));
  downloadCsv(header, body, "gew_overview_partners_portal");
}

function exportLegacyCsv(rows: OverviewLegacyPartner[]) {
  const header = [
    "Org Name (AR)",
    "Org Name (EN)",
    "Type",
    "City",
    "Coordinator",
    "Email",
    "Phone",
    "Collaborated Before",
    "Submitted",
  ];
  const body = rows.map((r) => [
    r.orgNameAr,
    r.orgNameEn ?? "",
    r.orgType,
    r.orgCity,
    r.coordinatorName ?? "",
    r.coordinatorEmail ?? "",
    r.coordinatorPhone ?? "",
    r.hasCollaboratedBefore ? "Yes" : "No",
    r.submittedAt ?? "",
  ].map(esc));
  downloadCsv(header, body, "gew_overview_partners_legacy");
}

function exportCatalystsCsv(rows: OverviewCatalyst[]) {
  const header = [
    "Catalyst",
    "Email",
    "Meetups",
    "Capacity (seats)",
    "Total Attendees",
    "Confirmed",
    "Waitlist",
    "Accepted",
    "Checked-in",
  ];
  const body = rows.map((r) => [
    r.hostName,
    r.hostEmail ?? "",
    r.meetupCount,
    r.totalCapacity,
    r.totalAttendees,
    r.confirmedCount,
    r.waitlistCount,
    r.acceptedCount,
    r.checkedInCount,
  ].map(esc));
  downloadCsv(header, body, "gew_overview_catalysts");
}
